import type { PatientCreateInput, PatientDbRecord } from "../schemas/patient.schema";
import { ROUTES } from "../constants";
import { apiRequest } from "./api.client";
import { db } from "../db/schema";
import { unwrapFromStorage } from "../db/encryption-bridge";
import type { PatientRecord } from "../db/db.types";
import { isPermissionDenied } from "../utils/network";

// Re-export for backwards-compat with existing catch blocks.
export { ApiError as PatientApiError } from "./api.errors";

export async function createPatient(data: PatientCreateInput): Promise<PatientDbRecord> {
  return apiRequest<PatientDbRecord>(ROUTES.PATIENTS, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getPatientById(patientId: string): Promise<PatientDbRecord> {
  return apiRequest<PatientDbRecord>(ROUTES.PATIENT(patientId));
}

export async function getPatientByDisplayId(displayId: string): Promise<PatientDbRecord> {
  try {
    return await apiRequest<PatientDbRecord>(`/patients/by-display-id/${displayId}`);
  } catch (err) {
    // A 403 is an answer, not a connectivity failure. Rethrow so /patients/PAT-XXXXXX
    // refuses instead of resolving the UUID out of the local cache — otherwise a
    // revoked patients.view still leaks "this display_id exists in this clinic",
    // and the caller reports it as "Patient not found", which is a lie.
    if (isPermissionDenied(err)) throw err;

    // Offline fallback: look up by the display_id plaintext index stored in Dexie.
    try {
      const row = await db.patients.where("display_id").equals(displayId).first();
      if (row) {
        const p = await unwrapFromStorage<PatientRecord>(row as unknown as Record<string, unknown>);
        return { ...p, patient_id: p.patient_id } as unknown as PatientDbRecord;
      }
    } catch (idbErr) {
      // DEK unavailable (OperationError = wrong key, fetch error = backend unreachable).
      // Propagate with "offline" in the message so PatientPage shows the right toast.
      throw new Error(
        `Patient data unavailable offline: encryption key missing (${idbErr instanceof Error ? idbErr.message : idbErr})`,
      );
    }
    throw new Error(`Patient not found offline: ${displayId}`);
  }
}

export async function updatePatient(
  patientId: string,
  updates: Partial<PatientCreateInput>
): Promise<PatientDbRecord> {
  return apiRequest<PatientDbRecord>(ROUTES.PATIENT(patientId), {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

export type PatientStatusFilter = "active" | "inactive" | "all";

export async function getPatients(status: PatientStatusFilter = "active"): Promise<PatientDbRecord[]> {
  return apiRequest<PatientDbRecord[]>(`${ROUTES.PATIENTS}?status=${status}`);
}

export async function searchPatients(q: string, limit = 8): Promise<PatientDbRecord[]> {
  return apiRequest<PatientDbRecord[]>(ROUTES.PATIENT_SEARCH(q, limit));
}

export async function deletePatient(patientId: string): Promise<void> {
  return apiRequest<void>(ROUTES.PATIENT(patientId), { method: "DELETE" });
}

export async function reactivatePatient(patientId: string): Promise<PatientDbRecord> {
  return apiRequest<PatientDbRecord>(`${ROUTES.PATIENT(patientId)}/reactivate`, { method: "PATCH" });
}

export async function permanentlyDeletePatient(patientId: string): Promise<void> {
  return apiRequest<void>(`${ROUTES.PATIENT(patientId)}/permanent`, { method: "DELETE" });
}

export const PatientAPI = {
  create: createPatient,
  getById: getPatientById,
  update: updatePatient,
  getAll: getPatients,
  delete: deletePatient,
  reactivate: reactivatePatient,
  permanentlyDelete: permanentlyDeletePatient,
};
